import BankCardType from '~/enums/bank-card-type'

export enum OrderPaymentMethod {
  Other = '0',
  TinkoffBank = '1',
  YooKassa = '2',
  RaiffeisenBank = '3',
  Dolyame = '4',
  Payselection = '5',
  DavayDelit = '6',
  CloudPayments = '7',
  CloudPaymentsSbp = '7-sbp', // It is a virtual method. Does not exist on the backend.
}

const {
  CloudPayments,
  DavayDelit,
  Dolyame,
  Other,
  Payselection,
  RaiffeisenBank,
  CloudPaymentsSbp,
  TinkoffBank,
  YooKassa
} = OrderPaymentMethod

const bankCardTypeMapping = new Map([
  [CloudPayments, BankCardType.Russian],
  [CloudPaymentsSbp, BankCardType.Russian],
  [DavayDelit, BankCardType.Russian],
  [Dolyame, BankCardType.Russian],
  [Other, BankCardType.Any],
  [Payselection, BankCardType.International],
  [RaiffeisenBank, BankCardType.International],
  [TinkoffBank, BankCardType.Russian],
  [YooKassa, BankCardType.Any]
])

const descriptionMapping = new Map([
  [CloudPayments, 'Через CloudPayments'],
  [CloudPaymentsSbp, 'Доставим бесплатно'],
  [DavayDelit, 'Через Давай делить'],
  [Dolyame, 'Через Долями'],
  [Other, null],
  [Payselection, 'Через Payselection'],
  [RaiffeisenBank, 'Через Райффайзен Банк'],
  [TinkoffBank, 'Через Т-Банк'],
  [YooKassa, 'Через ЮKassa']
])

const iconDescriptionMapping = new Map([
  [CloudPayments, '—\xA0Российские карты: Visa, Mastercard и\xA0Мир.\n—\xA0Зарубежные карты: Мир, Белкарт-Мир.\n—\xA0Сервисы: SberPay, T-Pay и\xA0Mir Pay.'],
  [CloudPaymentsSbp, 'Доставим бесплатно в ПВЗ/постамат вашего города.'],
  [DavayDelit, '—\xA0Российские карты: Visa, Mastercard и\xA0Мир.'],
  [Dolyame, '—\xA0Российские карты: Visa, Mastercard и\xA0Мир.\n—\xA0Оплатите\xA025% от\xA0стоимости покупки, а\xA0оставшиеся 3\xA0части спишутся автоматически с\xA0шагом в\xA02\xA0недели. Без процентов и\xA0комиссий, как обычная оплата картой.'],
  [Other, null],
  [Payselection, '—\xA0Российские карты: Visa, Mastercard и\xA0Мир.\n—\xA0Зарубежные карты: Mastercard.'],
  [RaiffeisenBank, '—\xA0Российские карты: Visa, Mastercard, Мир и\xA0UnionPay.\n—\xA0Зарубежные карты: Мир.'],
  [TinkoffBank, '—\xA0Российские карты: Visa, Mastercard и\xA0Мир.\n—\xA0Зарубежные карты: Мир.\n—\xA0Сервисы: СБП и\xA0Tinkoff Pay.'],
  [YooKassa, '—\xA0Российские карты: Visa, Mastercard, Мир и\xA0JCB.\n—\xA0Зарубежные карты: Мир, АПРА, Белкарт-Мир, Корти Милли и\xA0Express Pay.\n—\xA0Сервисы: СБП, SberPay и\xA0ЮMoney.']
])

const iconNameMapping = new Map([
  [CloudPayments, 'payment-methods/cloudpayments'],
  [CloudPaymentsSbp, 'payment-methods/sbp'],
  [DavayDelit, 'payment-methods/davay-delit'],
  [Dolyame, 'payment-methods/dolyame'],
  [Other, null],
  [Payselection, 'payment-methods/payselection'],
  [RaiffeisenBank, 'payment-methods/raiffeisen-bank'],
  [TinkoffBank, 'payment-methods/tinkoff-bank'],
  [YooKassa, 'payment-methods/yoo-kassa']
])

const iconName2Mapping = new Map([
  [CloudPayments, 'payment-methods-2/cloudpayments'],
  [CloudPaymentsSbp, 'payment-methods-2/sbp'],
  [DavayDelit, 'payment-methods/davay-delit'], // TODO
  [Dolyame, 'payment-methods-2/dolyame'],
  [Other, null],
  [Payselection, 'payment-methods/payselection'], // TODO
  [RaiffeisenBank, 'payment-methods/raiffeisen-bank'], // TODO
  [TinkoffBank, 'payment-methods/tinkoff-bank'], // TODO
  [YooKassa, 'payment-methods/yoo-kassa'] // TODO
])

const nameMapping = new Map([
  [CloudPayments, 'CloudPayments'],
  [CloudPaymentsSbp, 'CloudPayments (СБП)'],
  [DavayDelit, 'Давай делить'],
  [Dolyame, 'Долями'],
  [Other, 'Другой'],
  [Payselection, 'Payselection'],
  [RaiffeisenBank, 'Райффайзен Банк'],
  [TinkoffBank, 'Тинькофф Банк'],
  [YooKassa, 'ЮKassa']
])

const name2Mapping = new Map([
  [CloudPayments, 'Банковской картой'],
  [CloudPaymentsSbp, 'СБП'],
  [DavayDelit, 'Равными частями'],
  [Dolyame, 'Равными долями'],
  [Other, 'Другой'],
  [Payselection, 'Банковской картой'],
  [RaiffeisenBank, 'Банковской картой'],
  [TinkoffBank, 'Банковской картой'],
  [YooKassa, 'Банковской картой']
])

export const OrderPaymentMethodUtils = {
  sortedMembers: [
    Other,
    CloudPayments,
    CloudPaymentsSbp,
    YooKassa,
    TinkoffBank,
    RaiffeisenBank,
    Payselection,
    Dolyame,
    DavayDelit
  ],
  getBankCardType: (x: OrderPaymentMethod): BankCardType => bankCardTypeMapping.get(x)!,
  getDescription: (x: OrderPaymentMethod): string => descriptionMapping.get(x)!,
  getIconDescription: (x: OrderPaymentMethod): string => iconDescriptionMapping.get(x)!,
  getIconName: (x: OrderPaymentMethod): string => iconNameMapping.get(x)!,
  getIconName2: (x: OrderPaymentMethod): string => iconName2Mapping.get(x)!,
  getName: (x: OrderPaymentMethod): string => nameMapping.get(x)!,
  getName2: (x: OrderPaymentMethod): string => name2Mapping.get(x)!
}
